import { formatApiPrice, formatLocation, useRouter } from '@application/utils';
import { useReturnParams } from '@application/utils/hooks/useReturnParams';
import type { LitterListGetLittersQuery } from '@gql/graphql';
import type { CardItemLitterData } from '@presentation/components';
import { PLACEHOLDER_IMAGE_DOG } from '@presentation/constants';
import dayjs from 'dayjs';
import { useMemo } from 'react';

export const useFormatLitters = (litters?: LitterListGetLittersQuery['findManyLitter'] | null) => {
  const { routeLocationDescriptor, language, translate } = useRouter();
  const { returnBreed, returnSearch } = useReturnParams();
  const preloadImagesCount = 2;
  const formattedLitters: CardItemLitterData[] | undefined = useMemo(() => {
    if (!litters || !litters.length) return;
    return litters.map((litter, index) => {
      let breed = `${litter.dogBreed?.translations[0]?.name ?? ''} ${translate(
        'glossary.puppies',
      )}`;
      if (language === 'fr') {
        breed = `${translate('glossary.puppies')} ${litter.dogBreed?.translations[0]?.name ?? ''}`;
      }
      return {
        id: litter.id,
        breed,
        image: litter.imgs?.[0]?.small ?? PLACEHOLDER_IMAGE_DOG,
        price: formatApiPrice({ ...litter }),
        hasPedigreeFciBadge: litter.pedigree === 'FCI',
        hasPedigreeBadge: !(
          !litter.pedigree ||
          litter.pedigree === 'no_pedigree' ||
          litter.pedigree === 'FCI'
        ),
        location: formatLocation(
          {
            country: litter.breeder.account?.address?.country,
            region: litter.breeder.account?.address?.region,
          },
          translate,
        ),
        ...(!litter.born
          ? {
              futureLitter: {
                month: dayjs(litter.birthday)?.format?.('MMMM') ?? '-',
                year: dayjs(litter.birthday)?.format?.('YYYY') ?? '-',
              },
            }
          : {}),
        link: routeLocationDescriptor('litter', {
          params: [litter.translations?.[0]?.url ?? ''],
          state: {
            returnSearch,
            returnBreed,
          },
        }),
        lazyLoadImage: index >= preloadImagesCount,
      } as CardItemLitterData;
    });
  }, [language, litters, returnBreed, returnSearch, routeLocationDescriptor, translate]);

  const imagesToPreload: string[] = useMemo(() => {
    if (!formattedLitters?.length) return [];
    return formattedLitters.slice(0, preloadImagesCount).map((litter) => litter.image as string);
  }, [formattedLitters, preloadImagesCount]);

  return { formattedLitters, imagesToPreload };
};
